import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useNavigate } from "react-router-dom"

import { Grid, Link } from "@mui/material"
import { Button, NotificationUtils, Snackbar, Tooltip, Typography } from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"

import { SidAPI } from "../../../services/SidAPI"
import type { VerificationContainerProps, VerificationProps } from "../interfaces"

import styles from "./VerificationScreen.module.scss"

/**
 * Verification component that handles email verification UI states and resend verification functionality
 *
 * @param {string} email - Email address to send verification link to
 * @param {boolean} isVerificationSent - Indicates if verification email has been sent
 * @param {function} createAccount - Callback function to navigate to signup screen
 * @param {boolean} isLoading - Indicates if verification email is being sent
 * @param {string} errorMessage - Error message to show if verification email fails to send
 * @param {function} handleClick - Callback function to resend verification email
 * @returns {ReactElement} Verification screen with different states based on verification status
 */
export function Verification(props: Readonly<VerificationProps>): ReactElement {
  const { isVerificationSent, email, createAccount, isLoading, errorMessage, handleClick } = props

  const { t } = useTranslation("auth")
  const navigate = useNavigate()

  return (
    <Grid item container className={styles.mainContainer}>
      {/**
       * Verification screens snackbars
       * 1. email sent to account successfully
       * 2. Network error
       */}
      {!errorMessage && <Snackbar fullWidth severity="success" description={t("verification.success")} />}

      {errorMessage && errorMessage === "Network Error" && (
        <Snackbar fullWidth severity="error" description={t("verification.error")} />
      )}

      <Grid container className={styles.formContainer}>
        <Grid item container xs={12} justifyContent="start" gap={1}>
          {isVerificationSent ? (
            <Fragment>
              <Typography variant="h1-bold">{t("verification.activateAccount")}</Typography>

              <Grid item container justifyContent="start" wrap="nowrap" gap={0.5}>
                <Typography variant="p" noWrap>
                  {t("verification.checkEmail")}
                </Typography>

                <Grid className={styles.verificationScreenEmail}>
                  <Tooltip title={email} width="100%" placement="bottom">
                    <Typography
                      variant="a"
                      noWrap
                      style={{ textOverflow: "ellipsis", width: "100%" }}
                      data-testid="email-sent"
                    >
                      {email}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </Fragment>
          ) : (
            <Fragment>
              <Typography variant="h2-bold">{t("verification.accountNotVerified")}</Typography>

              <Grid item container justifyContent="start" wrap="nowrap" gap={0.5}>
                <Typography variant="p" noWrap>
                  {t("verification.verificationEmailSent")}
                </Typography>

                <Grid className={styles.verificationScreenEmail}>
                  <Tooltip title={email} width="100%" placement="bottom">
                    <Typography
                      variant="a"
                      noWrap
                      style={{ textOverflow: "ellipsis", width: "100%" }}
                      data-testid="email-not-sent"
                    >
                      {email}
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </Fragment>
          )}
        </Grid>

        <Grid item container xs={12} justifyContent="start" gap={1}>
          {isVerificationSent ? (
            <Fragment>
              <Button fullWidth id="return-to-login" variant="primary" type="submit" onClick={() => navigate("/")}>
                {t("verification.returnToLogin")}
              </Button>

              <Button
                fullWidth
                id="resend-verification"
                variant="secondary"
                type="submit"
                onClick={handleClick}
                disabled={isLoading}
                isLoading={isLoading}
              >
                {t("verification.resend")}
              </Button>
            </Fragment>
          ) : (
            <Button
              fullWidth
              id="send-verification"
              variant="primary"
              type="submit"
              onClick={handleClick}
              disabled={isLoading}
              isLoading={isLoading}
            >
              {t("verification.sendLink")}
            </Button>
          )}
        </Grid>
      </Grid>

      <Grid item container xs={12} justifyContent="center">
        <Typography variant="p" variantColor={2}>
          {t("verification.wrongEmail")}{" "}
          {/* TODO:: replace with SUI anchor component in the revamp leaving the styles inline to remember to remove them when revamping */}
          <Link onClick={() => (createAccount ? createAccount() : navigate("/register"))} className={styles.link}>
            {t("verification.createAccount")}
          </Link>
        </Typography>
      </Grid>
    </Grid>
  )
}

/**
 * Verification component that handles email verification UI states and resend verification functionality
 *
 * @param {string} email - Email address to send verification link to
 * @param {boolean} isPostSignup - Indicates if user came from signup flow (defaults to false)
 * @param {function} createAccount - Callback function to navigate to signup screen
 * @returns {ReactElement} Verification screen with different states based on verification status
 */
export function VerificationContainer(props: Readonly<VerificationContainerProps>): ReactElement {
  const { email, isPostSignup = false, createAccount } = props
  const { t } = useTranslation("auth")

  /**
   * Tracks whether a verification email has been sent
   * - true: shows "Activate your account" view with resend option
   * - false: shows "Your account is not verified yet" view with initial send option
   */
  const [isVerificationSent, setIsVerificationSent] = useState<boolean>(isPostSignup)

  /**
   * Mutation hook for resending verification emails
   * - Automatically sets isVerificationSent on success
   * - Shows error toast on failure
   * - Retries failed attempts up to 3 times
   */
  const resendVerificationMutation = useMutation<AxiosResponse<void>, AxiosError, string>(
    SidAPI.resendVerificationLink,
    {
      onSuccess: () => setIsVerificationSent(true),
      onError: () =>
        NotificationUtils.toast(t("verification.resendError"), {
          severity: "error",
        }),
      retry: 3,
    },
  )

  const handleClick = async (): Promise<void> => {
    await resendVerificationMutation.mutateAsync(email)
  }

  return (
    <Verification
      isVerificationSent={isVerificationSent}
      email={email}
      createAccount={createAccount}
      isLoading={resendVerificationMutation.isLoading}
      errorMessage={resendVerificationMutation.error?.message}
      handleClick={handleClick}
    />
  )
}
